import ImageLayer from "ol/layer/Image";
import ImageWMS from "ol/source/ImageWMS";
import WMTS from "ol/source/WMTS.js";
import WMTSTileGrid from "ol/tilegrid/WMTS.js";
import TileLayer from "ol/layer/Tile.js";
import { get as getProjection, transform } from "ol/proj.js";
import { GeoJSON, WKT } from "ol/format";
import { getWidth } from "ol/extent.js";
import VectorSource from "ol/source/Vector";
import VectorLayer from "ol/layer/Vector";
import { bbox as bboxStrategy } from "ol/loadingstrategy.js";
import { transformExtent } from "ol/proj";
import store from "../../stores/store";
import { walk } from "react-sortable-tree";
import Draw, { createRegularPolygon } from "ol/interaction/Draw";
import {
  featureInfoRequest,
  fetch,
  getProjectionConfig,
  key,
} from "../../lib/utils";
import { ScaleLine } from "ol/control.js";
import {
  filterFeatureRecordFromAttribute,
  formatSearchResultsFromServiceResponse,
} from "../../lib/featureUtils";
import { Circle as CircleStyle, Fill, Stroke, Style, Text } from "ol/style.js";
import { createStringXY } from "ol/coordinate";

export function getLayerById(id) {
  let layer = null;
  window["map"]?.getLayers()?.forEach((el) => {
    if (el.get("layerId") === id) {
      layer = el;
    }
  });
  return layer;
}

export function getAllLayerIds() {
  let layers = [];
  window["map"]?.getLayers().forEach((el) => {
    if (el.get("layerId")) {
      layers.push(el.get("layerId"));
    }
  });
  return layers;
}

export function removeMapAllLayers() {
  const layers = [
    ...window["map"]
      .getLayers()
      .getArray()
      .filter((el) => {
        return el.get("layerId");
      }),
  ];
  layers.forEach((layer) => window["map"].removeLayer(layer));

  window["drawLayer"]?.setZIndex(2000);
  window["editLayer"]?.setZIndex(2500);
  window["measurementLayer"]?.setZIndex(3000);
  window["printLayer"]?.setZIndex(3500);
  try {
    window["formsPointDraw"]?.setZIndex(6500);
  } catch (e) {}
}

export function sortMapLayers(layerTree) {
  walk({
    treeData: layerTree,
    getNodeKey: ({ treeIndex }) => treeIndex,
    ignoreCollapsed: true,
    callback: (rowInfoIter) => {
      if (rowInfoIter.node?.type === "layer") {
        setLayerZIndex(
          { id: rowInfoIter.node?.id },
          1000 - rowInfoIter.treeIndex
        );
      }
    },
  });

  window["drawLayer"]?.setZIndex(2000);
  window["editLayer"]?.setZIndex(2500);
  window["measurementLayer"]?.setZIndex(3000);
  window["printLayer"]?.setZIndex(3500);
  try {
    window["formsPointDraw"]?.setZIndex(6500);
  } catch (e) {}
}

export function getMapCenter() {
  return window["map"] ? window["map"].getView().getCenter() : null;
}

export function getMapProjection() {
  return window["map"]
    ? window["map"].getView().getProjection().getCode()
    : null;
}

export function getSize() {
  return window["map"] ? window["map"].getSize() : null;
}

export function mapRemoveControl(controlId) {
  window["map"]?.getControls().forEach((el) => {
    if (el.controlId === controlId) {
      try {
        window["map"].removeControl(el);
      } catch (e) {}
    }
  });
}

export function arrangeLayersZIndex() {
  window["map"]?.getLayers().forEach((el, idx) => {
    if (el.get("layerId")) {
      el.setZIndex(2000 - idx);
    }
  });
}

export function mapGetControl(controlId) {
  let control = null;
  window["map"]?.getControls().forEach((el) => {
    if (el.controlId === controlId) {
      control = el;
    }
  });

  return control;
}

export function mapAddControl(control) {
  window["map"].addControl(control);
}

export function updateMapMousePosition(projection) {
  let projectionConfig = getProjectionConfig(projection);

  window["map"].getControls().forEach((el) => {
    if (el.controlId === "mouseposition") {
      el.setCoordinateFormat(createStringXY(projectionConfig.numDigits));
      el.setProjection(projection);
    }
  });
}

export function getMapCenterProjected(projection) {
  if (!window["map"]) {
    return null;
  }

  let projectionConfig = getProjectionConfig(projection);

  let center = transform(
    window["map"].getView().getCenter(),
    window["map"].getView().getProjection(),
    projection
  );

  return [
    center[0].toFixed(projectionConfig.numDigits),
    center[1].toFixed(projectionConfig.numDigits),
  ];
}

export function getZoomLevel() {
  return window["map"] ? window["map"].getView().getZoom() : null;
}

export function mapScaleLine(scaleSettings) {
  window["map"].getControls().forEach((el) => {
    if (el?.controlId == "scaleline") {
      window["map"].removeControl(el);
    }
  });

  if (scaleSettings.show) {
    let control = new ScaleLine({
      units: scaleSettings.units,
      bar: scaleSettings.type === "scalebar" ? false : true,
      steps: parseInt(scaleSettings.steps, 10),
      text: true,
      minWidth: 140,
    });

    control.controlId = "scaleline";

    window["map"].addControl(control);
  }
}

export function getFeatureInfo(e, layers, sources, store) {
  const pixels = window["map"].getPixelFromCoordinate(e.coordinate);

  const postData = [];

  Object.keys(layers)?.forEach((layerId) => {
    let layer = layers[layerId];
    let source = sources[layer.source.id];

    if (layer.visibility && layer.queryable) {
      if (source?.type === "GEOJSON") {
        getLayerById(layer.id)
          ?.getFeatures(e.pixel)
          .then(function (features) {
            let results = [];

            features?.forEach((feature) => {
              let properties = {};

              Object.keys(feature?.values_)?.forEach((el) => {
                if (el !== "geometry") {
                  properties[el] = feature?.values_[el];
                }
              });

              results.push(properties);
            });

            if (results.length > 0) {
              store.dispatch({
                type: "results/featureInfoResults/add",
                payload: {
                  id: layer.id,
                  data: [...results],
                  params: {
                    featureInfo: {
                      coords: [...e.coordinate],
                      projection: window["map"]
                        .getView()
                        .getProjection()
                        .getCode(),
                    },
                    layerId: layer.id,
                    searchKey: searchKey,
                  },
                },
              });
            }
          });
      } else {
        postData.push(featureInfoRequest(layer, source));
      }
    }
  });

  const searchKey = key();

  if (postData.length > 0) {
    const mapBBOX = window["map"]
      .getView()
      .calculateExtent(window["map"].getSize());

    let featureInfoParamas =
      "?_width=" +
      Math.round(window["map"].getSize()[0]) +
      "&_height=" +
      Math.round(window["map"].getSize()[1]) +
      "&_x=" +
      Math.round(pixels[0]) +
      "&_y=" +
      Math.round(pixels[1]) +
      "&_bbox=" +
      mapBBOX.join(",") +
      "&_srs=" +
      window["map"].getView().getProjection().getCode() +
      "&_lon=" +
      e.coordinate[0] +
      "&_lat=" +
      e.coordinate[1];

    fetch(
      window["appConfig"]()["api"](featureInfoParamas),
      "POST",
      {},
      postData,
      (resp) => {
        if (resp.data) {
          resp.data?.forEach((layerResults, index) => {
            let results = formatSearchResultsFromServiceResponse(
              resp,
              index
            )?.map((el) => {
              el._srsName =
                layers[layerResults._response._layerId].nativeProjection;
              return el;
            });

            if (results.length > 0) {
              if (
                layers[layerResults._response._layerId].attributes.length == 0
              ) {
                let attributes = Object.keys(results[0])?.map((el) => {
                  let visible =
                    [
                      "_feature",
                      "_featureUrl",
                      "_featureId",
                      "_srsName",
                      "_featureGeomFormat",
                      "_layerId",
                    ].indexOf(el) < 0
                      ? true
                      : false;

                  return {
                    name: el,
                    type: "string",
                    title: el,
                    value: null,
                    editor: null,
                    sortable: true,
                    order: 0,
                    visible: visible,
                    searchable: true,
                    isGeometry: false,
                    isVisible: visible,
                    showOnSummary: false,
                  };
                });

                store.dispatch({
                  type: "layer/update",
                  payload: {
                    property: "attributes",
                    id: layerResults._response._layerId,
                    value: attributes,
                  },
                });
              }
              store.dispatch({
                type: "results/featureInfoResults/add",
                payload: {
                  id: layerResults._response._layerId,
                  data: [...results],
                  params: {
                    featureInfo: {
                      coords: [...e.coordinate],
                      projection: window["map"]
                        .getView()
                        .getProjection()
                        .getCode(),
                    },
                    layerId: layerResults._response._layerId,
                    searchKey: searchKey,
                  },
                },
              });
            }
          });
        }
      }
    );
  }
}

export function zoomToExtent(layer) {
  window["map"]
    .getView()
    .fit(
      transformExtent(
        layer.bbox,
        "EPSG:4326",
        window["map"].getView().getProjection().getCode()
      )
    );
}

export function draw(type, onEnd) {
  let drawObject = null;

  if (type !== "clear") {
    if (type !== "Circle") {
      drawObject = new Draw({
        source: window["drawSource"],
        type: type,
      });
    } else {
      drawObject = new Draw({
        source: window["drawSource"],
        type: type,
        geometryFunction: createRegularPolygon(40),
      });
    }

    window["map"].addInteraction(drawObject);
    drawObject.on("drawend", function (e) {
      onEnd(e, draw);

      window["map"].removeInteraction(drawObject);
    });
  } else {
    window["map"].removeInteraction(drawObject);
    window["drawSource"].clear();
  }
}

export function setLayerProperty(layer, property, value) {
  if (layer) {
    getLayerById(layer.id)?.set(property, value);
  }
}

export function setLayerStyle(layer, style) {
  if (layer) {
    getLayerById(layer.id)?.setStyle(style);
  }
}

export function setSLD(layer, sldId, layerSLDs) {
  let groups = layerSLDs ? [...layerSLDs[sldId].groups] : [];

  switch (layer.source.type) {
    case "WMS":
      if (!layerSLDs) {
        setLayerSLDStyle(layer, sldId);
      } else {
        let sldBody = buildSLDFile(groups, layer);

        setLayerSLD(layer, sldBody);
      }

      break;

    case "WFS":
      setLayerStyle(layer, function (feature) {
        let style = null;
        groups?.forEach((group) => {
          let hasFilter = [];
          for (var i = 1; i < group.length; i++) {
            let filtered = filterFeatureRecordFromAttribute(
              feature.getProperties(),
              group[i].attribute,
              group[i].attributeOperator,
              group[i].attributeValue
            );

            hasFilter.push(filtered ? true : false);
          }

          let uniqueValues = hasFilter.filter(
            (value, index, array) => array.indexOf(value) === index
          );

          if (uniqueValues.length == 1 && uniqueValues[0] == true) {
            style = buildSLDStyle(group)[0];
          }
        });

        if (style) {
          return style;
        } else {
          return buildSLDStyle(groups[0])[0];
        }
      });
      break;

    case "GEOJSON":
      setLayerStyle(layer, function (feature) {
        let style = null;
        groups?.forEach((group) => {
          let hasFilter = [];
          for (var i = 1; i < group.length; i++) {
            let filtered = filterFeatureRecordFromAttribute(
              feature.getProperties(),
              group[i].attribute,
              group[i].attributeOperator,
              group[i].attributeValue
            );

            hasFilter.push(filtered ? true : false);
          }

          let uniqueValues = hasFilter.filter(
            (value, index, array) => array.indexOf(value) === index
          );

          if (uniqueValues.length == 1 && uniqueValues[0] == true) {
            style = buildSLDStyle(group, feature)[0];
          }
        });

        if (style) {
          return style;
        } else {
          return buildSLDStyle(groups[0])[0];
        }
      });
      break;
  }
}

export function setLayerParams(layer, params) {
  if (layer) {
    getLayerById(layer.id).getSource().updateParams(params);
  }
}

export function setLayerCQL(layer, params) {
  if (layer) {
    let layerParams = getLayerById(layer.id).getSource().getParams();
    layerParams.CQL_FILTER = params;
    getLayerById(layer.id).getSource().updateParams(layerParams);
  }
}

export function removeLayerCQL(layer) {
  if (layer) {
    let layerParams = getLayerById(layer.id).getSource().getParams();
    delete layerParams.CQL_FILTER;
    getLayerById(layer.id).getSource().updateParams(layerParams);
  }
}

export function setLayerSLD(layer, params) {
  if (layer) {
    removeLayerSLD(layer);
    let layerParams = getLayerById(layer.id).getSource().getParams();
    layerParams.SLD_BODY = params;
    getLayerById(layer.id).getSource().updateParams(layerParams);
  }
}

export function setLayerSLDStyle(layer, params) {
  if (layer) {
    removeLayerSLD(layer);
    let layerParams = getLayerById(layer.id).getSource().getParams();
    layerParams.STYLES = params;
    getLayerById(layer.id).getSource().updateParams(layerParams);
  }
}

export function removeLayerSLD(layer) {
  if (layer) {
    let layerParams = getLayerById(layer.id).getSource().getParams();
    delete layerParams.SLD_BODY;
    delete layerParams.STYLES;
    getLayerById(layer.id).getSource().updateParams(layerParams);
  }
}

export function getLayerParams(layer, param) {
  if (layer) {
    let layerParams = getLayerById(layer.id).getSource().getParams();
    return layerParams[param];
  }
}

export function setLayerTimeDimension(layer, params) {
  if (layer) {
    let layerParams = getLayerById(layer.id).getSource().getParams();
    layerParams.TIME = params;
    getLayerById(layer.id).getSource().updateParams(layerParams);
  }
}

export function removeLayerTimeDimension(layer) {
  if (layer) {
    let layerParams = getLayerById(layer.id).getSource().getParams();
    delete layerParams.TIME;
    getLayerById(layer.id).getSource().updateParams(layerParams);
  }
}
export function getLayerZIndex(layer) {
  if (layer) {
    return getLayerById(layer.id)?.getZIndex();
  }
}

export function getLayerOpacity(layer) {
  if (layer) {
    return getLayerById(layer.id)?.getOpacity();
  }
}

export function setLayerOpacity(layer, value) {
  if (layer) {
    getLayerById(layer.id)?.setOpacity(value);
  }
}

export function getLayerVisibility(layer) {
  if (layer) {
    return getLayerById(layer.id)?.getVisible();
  }
}

export function setLayerVisibility(layer, value) {
  if (layer) {
    getLayerById(layer.id)?.setVisible(value);
  }
}

export function setLayerZIndex(layer, value) {
  if (layer) {
    getLayerById(layer.id)?.setZIndex(value);
  }
}

export function removeLayerById(id) {
  window["map"].removeLayer(getLayerById(id));
}

export function buildSLDFile(groups, layer) {
  let file =
    "<?xml version='1.0' encoding='UTF-8'?><sld:StyledLayerDescriptor xmlns='http://www.opengis.net/ogc' xmlns:sld='http://www.opengis.net/sld' xmlns:ogc='http://www.opengis.net/ogc' xmlns:gml='http://www.opengis.net/gml' xmlns:xsi='http://www.w3.org/2001/XMLSchema-instance' version='1.0.0' xsi:schemaLocation='http://www.opengis.net/sld http://schemas.opengis.net/sld/1.0.0/StyledLayerDescriptor.xsd'><sld:NamedLayer><sld:Name>".concat(
      layer.name,
      "</sld:Name><sld:UserStyle><sld:Title>Title</sld:Title><sld:Abstract>Abstract</sld:Abstract><sld:FeatureTypeStyle>"
    );

  groups.forEach((group) => {
    let configGroup = group[0];
    file += "<sld:Rule>";
    file += "<sld:Name>" + key() + "</sld:Name>";
    file += "<sld:Title>" + configGroup["ruleTitle"] + "</sld:Title>";
    file += "<sld:Abstract>" + configGroup["ruleAbstract"] + "</sld:Abstract>";

    if (configGroup["labelAttribute"]) {
      file +=
        "<sld:TextSymbolizer><sld:Label><ogc:PropertyName>" +
        configGroup["labelAttribute"] +
        "</ogc:PropertyName></sld:Label>";

      file += "<sld:Font>";
      file += configGroup["labelAttributeFontFamily"]
        ? "<sld:CssParameter name='font-family'>" +
          configGroup["labelAttributeFontFamily"] +
          "</sld:CssParameter>"
        : "";
      file += configGroup["labelAttributeFontSize"]
        ? "<sld:CssParameter name='font-size'>" +
          configGroup["labelAttributeFontSize"] +
          "</sld:CssParameter>"
        : "";
      file += configGroup["labelFontColor"]?.hex
        ? "<sld:CssParameter name='font-color'>" +
          configGroup["labelFontColor"].hex +
          "</sld:CssParameter>"
        : "";
      file += "</sld:Font>";

      file += "</sld:TextSymbolizer>";
    }

    file += "<ogc:Filter>";
    file += "<ogc:And>";
    for (var i = 1; i < group.length; i++) {
      let criteria = group[i];
      let attribute = "<ogc:PropertyName>".concat(
        criteria["attribute"],
        "</ogc:PropertyName><ogc:Literal>",
        criteria["attributeValue"],
        "</ogc:Literal>"
      );

      switch (criteria["attributeOperator"]) {
        case "=":
          file += "<ogc:PropertyIsEqualTo>".concat(
            attribute,
            "</ogc:PropertyIsEqualTo>"
          );
          break;

        case "!=":
          file += "<ogc:PropertyIsNotEqualTo>".concat(
            attribute,
            "</ogc:PropertyIsNotEqualTo>"
          );
          break;
        case ">=":
          file += "<ogc:PropertyIsGreaterThanOrEqualTo>".concat(
            attribute,
            "</ogc:PropertyIsGreaterThanOrEqualTo>"
          );
          break;

        case ">":
          file += "<ogc:PropertyIsGreaterThan>".concat(
            attribute,
            "</ogc:PropertyIsGreaterThan>"
          );
          break;

        case "<=":
          file += "<ogc:PropertyIsLessThanOrEqualTo>".concat(
            attribute,
            "</ogc:PropertyIsLessThanOrEqualTo>"
          );
          break;

        case "<":
          file += "<ogc:PropertyIsLessThan>".concat(
            attribute,
            "</ogc:PropertyIsLessThan>"
          );
          break;

        case "like":
          file += "<ogc:PropertyIsLike>".concat(
            attribute,
            "</ogc:PropertyIsLike>"
          );
          break;

        case "null":
          file += "<ogc:PropertyIsNull>".concat(
            attribute,
            "</ogc:PropertyIsNull>"
          );
          break;
      }
    }
    file += "</ogc:And>";
    file += "</ogc:Filter>";

    switch (configGroup["geometryType"]) {
      case "point":
        file += "<sld:PointSymbolizer>";
        file += "<sld:Graphic>";
        file += "<sld:Mark>";

        file += configGroup["pointsymbol"]
          ? "<sld:WellKnownName>" +
            configGroup["pointsymbol"] +
            "</sld:WellKnownName>"
          : "";

        file += configGroup["fillColor"]?.hex
          ? '<sld:Fill><sld:CssParameter name="fill">' +
            configGroup["fillColor"].hex +
            '</sld:CssParameter><sld:CssParameter name="fill-opacity">' +
            configGroup["fillColor"].rgb.a +
            "</sld:CssParameter></sld:Fill>"
          : "";

        file += configGroup["borderColor"]?.hex
          ? '<sld:Stroke><sld:CssParameter name="stroke">' +
            configGroup["borderColor"].hex +
            '</sld:CssParameter><sld:CssParameter name="stroke-opacity">' +
            configGroup["borderColor"].rgb.a +
            '</sld:CssParameter><sld:CssParameter name="stroke-width">' +
            configGroup["borderSize"] +
            "</sld:CssParameter></sld:Stroke>"
          : "";

        file += "</sld:Mark>";
        file += "</sld:Graphic>";
        file += "</sld:PointSymbolizer>";
        break;

      case "polygon":
        file += "<sld:PolygonSymbolizer>";
        if (configGroup["polygonFill"] !== "fill") {
          file += "<sld:GraphicFill>";
          file += "<sld:Graphic>";
          file += "<sld:Mark>";

          file += configGroup["polygonFill"]
            ? "<sld:WellKnownName>" +
              configGroup["polygonFill"] +
              "</sld:WellKnownName>"
            : "";

          file += configGroup["fillColor"]?.hex
            ? '<sld:Stroke><sld:CssParameter name="stroke">' +
              configGroup["fillColor"].hex +
              '</sld:CssParameter><sld:CssParameter name="stroke-opacity">' +
              configGroup["fillColor"].rgb.a +
              "</sld:CssParameter></sld:Stroke>"
            : "";

          file += "</sld:Mark>";
          file += "</sld:Graphic>";
          file += "</sld:GraphicFill>";
        } else {
          file += configGroup["fillColor"]?.hex
            ? '<sld:Fill><sld:CssParameter name="fill">' +
              configGroup["fillColor"].hex +
              '</sld:CssParameter><sld:CssParameter name="fill-opacity">' +
              configGroup["fillColor"].rgb.a +
              "</sld:CssParameter></sld:Fill>"
            : "";
        }

        file += configGroup["borderColor"]?.hex
          ? '<sld:Stroke><sld:CssParameter name="stroke">' +
            configGroup["borderColor"].hex +
            '</sld:CssParameter><sld:CssParameter name="stroke-opacity">' +
            configGroup["borderColor"].rgb.a +
            '</sld:CssParameter><sld:CssParameter name="stroke-width">' +
            configGroup["borderSize"] +
            "</sld:CssParameter></sld:Stroke>"
          : "";

        file += "</sld:PolygonSymbolizer>";
        break;

      case "line":
        file += "<sld:LineSymbolizer>";

        file += configGroup["borderColor"]?.hex
          ? '<sld:Stroke><sld:CssParameter name="stroke">' +
            configGroup["borderColor"].hex +
            '</sld:CssParameter><sld:CssParameter name="stroke-opacity">' +
            configGroup["borderColor"].rgb.a +
            '</sld:CssParameter><sld:CssParameter name="stroke-width">' +
            configGroup["borderSize"] +
            "</sld:CssParameter></sld:Stroke>"
          : "";

        file += "</sld:LineSymbolizer>";
        break;
    }

    file += "</sld:Rule>";
  });

  file +=
    "</sld:FeatureTypeStyle></sld:UserStyle></sld:NamedLayer></sld:StyledLayerDescriptor>";

  return file;
}

export function generateSLDStyleFromAttributes(layer) {}

export function buildSLDStyle(sldGroup, feature) {
  let styles = [];

  let style = {};

  let configGroup = sldGroup[0];

  switch (configGroup.geometryType) {
    case "polygon":
      if (configGroup.borderColor) {
        if (configGroup.borderColor.rgb) {
          style["stroke"] = new Stroke({
            color: "rgba(".concat(
              configGroup.borderColor.rgb.r,
              ",",
              configGroup.borderColor.rgb.g,
              ",",
              configGroup.borderColor.rgb.b,
              ",",
              configGroup.borderColor.rgb.a,
              ")"
            ),
            width: configGroup.borderSize,
          });
        }
      }

      if (configGroup.fillColor) {
        if (configGroup.fillColor.rgb) {
          style["fill"] = new Fill({
            color: "rgba(".concat(
              configGroup.fillColor.rgb.r,
              ",",
              configGroup.fillColor.rgb.g,
              ",",
              configGroup.fillColor.rgb.b,
              ",",
              configGroup.fillColor.rgb.a,
              ")"
            ),
          });
        }
      }

      if (configGroup.labelAttribute) {
        style["text"] = new Text({
          font: configGroup.labelAttributeFontSize.concat(
            "px ",
            configGroup.labelAttributeFontFamily
          ),
          text: feature.get(configGroup.labelAttribute).toString(),
          fill: new Fill({
            color: configGroup.labelFontColor.hex,
          }),
        });
      }
      styles.push(new Style(style));
      break;
  }

  return styles;
}

export function addMapLayer(layer) {
  if (!getLayerById(layer.id) && layer.source) {
    switch (layer.source.type) {
      case "WMS":
        let wmsurlProxy = window["appConfig"]()["api"](
          "?proxy=raw&contenttype=".concat("image/png", "&url=")
        );

        const wmsLayer = new ImageLayer({
          source: new ImageWMS({
            url: layer.source.url,
            params: {
              LAYERS: layer.name,
            },
            ratio: layer.singleTile !== "undefined" ? layer.singleTile : 1,
          }),
        });

        wmsLayer.getSource().set("olcs.proxy", wmsurlProxy);
        wmsLayer.set("layerId", layer.id);
        wmsLayer.setOpacity(layer.opacity);
        wmsLayer.setVisible(layer.visibility);

        window["map"].addLayer(wmsLayer);

        return wmsLayer;
        break;

      case "WMTS":
        const projection = getProjection(layer.wmts.matrixEPSG);
        const projectionExtent = projection.getExtent();
        const size = getWidth(projectionExtent) / 256;
        const resolutions = [];
        for (let z = 0; z < layer.wmts.matrixIds.length; ++z) {
          resolutions.push(size / Math.pow(2, z));
        }

        const wmtsLayer = new TileLayer({
          source: new WMTS({
            url: layer.source.url,
            layer: layer.name,
            matrixSet: layer.wmts.matrixSet,
            format: "image/png",
            projection: layer.wmts.matrixEPSG,
            tileGrid: new WMTSTileGrid({
              origin: [
                parseFloat(layer.wmts.topLeftCornerX),
                parseFloat(layer.wmts.topLeftCornerY),
              ],
              resolutions: resolutions,
              matrixIds: layer.wmts.matrixIds,
            }),
          }),
        });

        wmtsLayer.set("layerId", layer.id);
        wmtsLayer.setOpacity(layer.opacity);
        wmtsLayer.setVisible(layer.visibility);
        window["map"].addLayer(wmtsLayer);

        return wmtsLayer;

        break;

      case "WFS":
        const url = new URL(
          "?service=WFS&version=1.1.0&request=GetFeature&typename=".concat(
            layer.name,
            "&outputFormat=application/json&srsname=",
            layer.nativeProjection,
            "&"
          ),
          layer.source.url
        ).toString();

        const vectorSource = new VectorSource({
          format: new GeoJSON(),
          url: function (extent) {
            return window["appConfig"]()["api"](
              "?proxy=raw&contenttype=application/json&url=".concat(
                url +
                  "bbox=" +
                  transformExtent(
                    extent,
                    window["map"].getView().getProjection().getCode(),
                    layer.nativeProjection
                  ).join(",") +
                  "," +
                  layer.nativeProjection
              )
            );
          },
          strategy: bboxStrategy,
        });

        const wfsLayer = new VectorLayer({
          source: vectorSource,
          style: new Style({
            fill: new Fill({
              color: "#".concat(layer.color),
              opacity: layer.opacity,
            }),
            stroke: new Stroke({
              color: "black",
              opacity: layer.opacity,
              lineDash: [10, 10],
              width: 2,
            }),
            image: new CircleStyle({
              radius: 5,
              stroke: new Stroke({
                color: "#".concat(layer.color),
              }),
              fill: new Fill({
                color: "#".concat(layer.color),
                opacity: layer.opacity,
              }),
            }),
          }),
          // style: {
          //   "stroke-color": "black",
          //   "fill-color": "#".concat(layer.color),
          //   "point-radius": 5,
          //   "fill-opacity": layer.opacity,
          //   "stroke-width": 1,
          //   "stroke-opacity": 1,
          // },
        });

        wfsLayer.set("layerId", layer.id);

        wfsLayer.setVisible(layer.visibility);
        wfsLayer.setOpacity(layer.opacity);
        window["map"].addLayer(wfsLayer);

        return wfsLayer;

        break;

      case "GEOJSON":
        let geoJsonFile = store.getState().sources[layer.source.id].url;

        const geoJSONSource = new VectorSource({
          features: new GeoJSON({
            dataProjection: layer.nativeProjection,
            featureProjection: window["map"]
              .getView()
              .getProjection()
              .getCode(),
          }).readFeatures(JSON.parse(geoJsonFile)),
        });

        const geoJSONLayer = new VectorLayer({
          source: geoJSONSource,
          style: new Style({
            fill: new Fill({
              color: "#".concat(layer.color),
              opacity: layer.opacity,
            }),
            stroke: new Stroke({
              color: "black",
              opacity: layer.opacity,
              lineDash: [10, 10],
              width: 2,
            }),
            image: new CircleStyle({
              radius: 5,
              stroke: new Stroke({
                color: "#".concat(layer.color),
              }),
              fill: new Fill({
                color: "#".concat(layer.color),
                opacity: layer.opacity,
              }),
            }),
          }),
          // style: {
          //   "stroke-color": "black",
          //   "fill-color": "#".concat(layer.color),
          //   "point-radius": 5,
          //   "fill-opacity": layer.opacity,
          //   "stroke-width": 1,
          //   "stroke-opacity": 1,
          // },
        });

        geoJSONLayer.set("layerId", layer.id);
        geoJSONLayer.setVisible(layer.visibility);
        geoJSONLayer.setOpacity(layer.opacity);
        window["map"].addLayer(geoJSONLayer);

        console.log("Sss", layer);
        console.log("Sss", geoJSONLayer);

        return geoJSONLayer;

        break;
    }
  }
}
